import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { HC } from "../hr/theme";
import { DataTable, Pager, cmoney, num, dt } from "./crmui";
import { Pill, Btn } from "../hr/ui";
import { toast } from "sonner";
import { Download, Plus, Search, UserPlus } from "lucide-react";

const COLUMNS = [
  ["name", "Customer"], ["id", "Customer ID"], ["phone", "Phone"], ["email", "Email"],
  ["location_name", "Preferred location"], ["registration_channel", "Channel"],
  ["crm_status", "Status"], ["loyalty_tier", "Tier"], ["points_available", "Points"],
  ["orders", "Orders"], ["spend", "Lifetime spend"], ["last_purchase", "Last purchase"],
  ["registered_at", "Registered"],
];

export default function CRMCustomers() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [q, setQ] = useState({
    search: "", location_id: "", status: sp.get("status") || "", tier: "", channel: "",
    verification: sp.get("verification") || "", consent: "", segment_id: "",
    min_spend: "", min_orders: "", registered_from: "", registered_to: "",
    last_purchase_from: "", last_purchase_to: "", category: "",
  });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);
  const [segments, setSegments] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", marketing_consent: false });
  const pageSize = 25;

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: pageSize };
    Object.entries(q).forEach(([k, v]) => { if (v !== "" && v !== false) params[k] = v; });
    api.get("/crm/customers", { params }).then(setData)
      .catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, [q, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/crm/segments/fields").then(setMeta).catch(() => {});
    api.get("/crm/segments").then(setSegments).catch(() => {});
  }, []);

  const set = (k, v) => { setPage(1); setQ((s) => ({ ...s, [k]: v })); };
  const sel = (label, key, options) => (
    <select value={q[key]} onChange={(e) => set(key, e.target.value)} data-testid={`cust-filter-${key}`}
      className="h-9 px-2 rounded-md border text-[12px]" style={{ borderColor: HC.border }}>
      <option value="">{label}</option>
      {options.map((o, i) => <option key={`${o.value}-${i}`} value={o.value}>{o.label}</option>)}
    </select>
  );

  const exportCsv = async () => {
    const params = new URLSearchParams();
    Object.entries(q).forEach(([k, v]) => { if (v !== "" && v !== false) params.set(k, v); });
    try {
      const res = await api.get(`/crm/customers/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = "crm-customers.csv"; a.click();
      toast.success("Export downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const create = async () => {
    if (!form.name) { toast.error("Customer name is required"); return; }
    try {
      const c = await api.post("/crm/customers", { ...form, registration_channel: "admin" });
      toast.success(`${c.name} created — verification pending`);
      setShowNew(false); setForm({ name: "", phone: "", email: "", marketing_consent: false });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="crm-customers-page">
      <div className="rounded-2xl bg-white border p-3 space-y-2" style={{ borderColor: HC.border }}>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-3" style={{ color: HC.soft }} />
            <input value={q.search} onChange={(e) => set("search", e.target.value)} data-testid="cust-search"
              placeholder="Search name, phone, email or ID"
              className="h-9 pl-8 pr-3 rounded-md border text-[12.5px] w-72" style={{ borderColor: HC.border }} />
          </div>
          {sel("All locations", "location_id", (meta?.locations || []).map((l) => ({ value: l.id, label: l.name })))}
          {sel("All statuses", "status", (meta?.statuses || []).map((s) => ({ value: s, label: s })))}
          {sel("All tiers", "tier", (meta?.tiers || []).map((t) => ({ value: t, label: t })))}
          {sel("All channels", "channel", (meta?.channels || []).map((c) => ({ value: c, label: c })))}
          {sel("Any verification", "verification", (meta?.verification || []).map((v) => ({ value: v, label: v })))}
          {sel("Any consent", "consent", [{ value: "true", label: "Consented" }, { value: "false", label: "No consent" }])}
          {sel("All segments", "segment_id", segments.map((s) => ({ value: s.id, label: s.name })))}
          {sel("Any category", "category", (meta?.categories || []).map((c) => ({ value: c, label: c })))}
          <div className="flex-1" />
          <Btn variant="outline" onClick={exportCsv} data-testid="cust-export"><Download size={14} /> Export</Btn>
          <Btn onClick={() => setShowNew(true)} data-testid="cust-new"><UserPlus size={14} /> New Customer</Btn>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[11px]" style={{ color: HC.soft }}>
          <span>Min spend</span>
          <input type="number" value={q.min_spend} onChange={(e) => set("min_spend", e.target.value)} data-testid="cust-min-spend"
            className="h-8 px-2 w-24 rounded-md border text-[12px]" style={{ borderColor: HC.border }} />
          <span>Min orders</span>
          <input type="number" value={q.min_orders} onChange={(e) => set("min_orders", e.target.value)} data-testid="cust-min-orders"
            className="h-8 px-2 w-20 rounded-md border text-[12px]" style={{ borderColor: HC.border }} />
          <span>Registered</span>
          <input type="date" value={q.registered_from} onChange={(e) => set("registered_from", e.target.value)} data-testid="cust-reg-from"
            className="h-8 px-2 rounded-md border text-[12px]" style={{ borderColor: HC.border }} />
          <input type="date" value={q.registered_to} onChange={(e) => set("registered_to", e.target.value)}
            className="h-8 px-2 rounded-md border text-[12px]" style={{ borderColor: HC.border }} />
          <span>Last purchase</span>
          <input type="date" value={q.last_purchase_from} onChange={(e) => set("last_purchase_from", e.target.value)} data-testid="cust-lp-from"
            className="h-8 px-2 rounded-md border text-[12px]" style={{ borderColor: HC.border }} />
          <input type="date" value={q.last_purchase_to} onChange={(e) => set("last_purchase_to", e.target.value)}
            className="h-8 px-2 rounded-md border text-[12px]" style={{ borderColor: HC.border }} />
        </div>
      </div>

      <DataTable testid="crm-customers-table" columns={COLUMNS} rows={data.rows}
        empty={loading ? "Loading customers…" : "No customers match these filters"}
        onRowClick={(r) => navigate(`/crm/customers/${r.id}`)}
        render={{
          id: (r) => r.id.slice(0, 8),
          spend: (r) => cmoney(r.spend),
          points_available: (r) => num(r.points_available),
          crm_status: (r) => <Pill status={r.crm_status} text={r.crm_status} />,
          loyalty_tier: (r) => <Pill status="active" text={r.loyalty_tier} />,
          last_purchase: (r) => dt(r.last_purchase),
          registered_at: (r) => dt(r.registered_at),
          email: (r) => r.email || "—",
        }} />
      <Pager page={page} pageSize={pageSize} total={data.total} onPage={setPage} testid="cust-pager" />

      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(51,34,30,0.45)" }}>
          <div className="bg-white rounded-2xl border w-full max-w-md p-5 space-y-3" style={{ borderColor: HC.border }} data-testid="cust-new-modal">
            <h3 className="font-brand font-bold text-lg" style={{ color: HC.text }}>New Customer</h3>
            {["name", "phone", "email"].map((f) => (
              <input key={f} placeholder={f === "name" ? "Full name *" : f} value={form[f]} data-testid={`cust-new-${f}`}
                onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                className="h-10 px-3 w-full rounded-md border text-[13px]" style={{ borderColor: HC.border }} />
            ))}
            <label className="flex items-center gap-2 text-[12.5px]" style={{ color: HC.text }}>
              <input type="checkbox" checked={form.marketing_consent} data-testid="cust-new-consent"
                onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} />
              Customer has given marketing consent
            </label>
            <div className="text-[11.5px]" style={{ color: HC.soft }}>
              New customers enter probation — the loyalty wallet activates after verification.
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Btn variant="outline" onClick={() => setShowNew(false)}>Cancel</Btn>
              <Btn onClick={create} data-testid="cust-new-save"><Plus size={14} /> Create</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
